import React, { useState } from "react";
import { MapPin, Phone, Navigation, Star, Clock, ChevronLeft, ChevronRight, Paintbrush } from "lucide-react";

interface Painter {
  id: string;
  title: string;
  description?: string;
  location?: string;
  distance?: number | string;
  category?: string;
  serviceData?: {
    rating?: number;
    user_ratings_total?: number;
    opening_hours?: { open_now: boolean };
    geometry?: { location: { lat: number; lng: number } };
  };
}

interface NearbyPainterCardProps {
  painter?: Painter;
  onViewDetails: (painter: Painter) => void;
}

// Dummy Phone Numbers
const PHONE_NUMBERS_MAP: Record<string, string> = {
  painter_1: "09035259196",
  painter_2: "08197754076",
  painter_3: "08792911729",
  painter_4: "08460214718",
};

// Dummy Images
const PAINTER_IMAGES_MAP: Record<string, string[]> = {
  painter_1: [
    "https://images.unsplash.com/photo-1589939705384-5185137a7f0f?w=800",
    "https://images.unsplash.com/photo-1562259949-e8e7689d7828?w=800",
  ],
  painter_2: [
    "https://images.unsplash.com/photo-1598300042247-d088f8ab3a91?w=800",
    "https://images.unsplash.com/photo-1572635148818-ef6fd45eb394?w=800",
  ],
  painter_3: [
    "https://images.unsplash.com/photo-1513467535987-fd81bc7d62f8?w=800",
    "https://images.unsplash.com/photo-1589939705384-5185137a7f0f?w=800",
  ],
  painter_4: [
    "https://images.unsplash.com/photo-1581858726788-75bc0f6a952d?w=800",
    "https://images.unsplash.com/photo-1598300042247-d088f8ab3a91?w=800",
  ],
};

const PAINTER_DESCRIPTIONS_MAP: Record<string, string> = {
  painter_1: "Expert house painting services for interior and exterior. Specializing in waterproof and 3D painting techniques.",
  painter_2: "Professional wall painting and texturing services for residential and commercial properties.",
  painter_3: "Quality painting contractors offering complete interior and exterior painting solutions.",
  painter_4: "Experienced painting professionals providing textured wall painting and complete home makeovers.",
};

const PAINTER_SERVICES = ["Interior", "Exterior", "Waterproof", "3D Painting"];

// Dummy Painter List
const DUMMY_PAINTERS: Painter[] = [
  {
    id: "painter_1",
    title: "Kansai Nerolac Paints Limited",
    description: PAINTER_DESCRIPTIONS_MAP["painter_1"],
    location: "Hyderabad Jubilee, Hyderabad",
    distance: 8.5,
    category: "House Painters",
    serviceData: {
      rating: 4.3,
      user_ratings_total: 52,
      opening_hours: { open_now: true },
      geometry: { location: { lat: 17.4374, lng: 78.4778 } },
    },
  },
  {
    id: "painter_2",
    title: "Rainbow Enterprises",
    description: PAINTER_DESCRIPTIONS_MAP["painter_2"],
    location: "Banjara Hills, Hyderabad",
    distance: 9.3,
    category: "House Painters",
    serviceData: {
      rating: 4.1,
      user_ratings_total: 66,
      opening_hours: { open_now: true },
      geometry: { location: { lat: 17.4174, lng: 78.4478 } },
    },
  },
  {
    id: "painter_3",
    title: "Kamlesh Painting Contractor",
    description: PAINTER_DESCRIPTIONS_MAP["painter_3"],
    location: "Yousufguda, Hyderabad",
    distance: 11.2,
    category: "House Painters",
    serviceData: {
      rating: 5.0,
      user_ratings_total: 1,
      opening_hours: { open_now: true },
      geometry: { location: { lat: 17.4274, lng: 78.4578 } },
    },
  },
  {
    id: "painter_4",
    title: "Bhaskar Rao Painting Contractors",
    description: PAINTER_DESCRIPTIONS_MAP["painter_4"],
    location: "Kompally, Rangareddy",
    distance: 6.0,
    category: "Painting Contractors",
    serviceData: {
      rating: 5.0,
      user_ratings_total: 25,
      opening_hours: { open_now: true },
      geometry: { location: { lat: 17.4474, lng: 78.4678 } },
    },
  },
];

const SinglePainterCard: React.FC<{ painter: Painter; onViewDetails: (painter: Painter) => void }> = ({ painter, onViewDetails }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const photos = PAINTER_IMAGES_MAP[painter.id] || [];
  const currentPhoto = photos[currentImageIndex];

  const handleNextImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev + 1) % photos.length);
  };

  const handlePrevImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev - 1 + photos.length) % photos.length);
  };

  const getRating = () => painter.serviceData?.rating?.toFixed(1) || null;
  const getUserRatingsTotal = () => painter.serviceData?.user_ratings_total || null;
  const isOpen = painter.serviceData?.opening_hours?.open_now;

  const handleCall = (e: React.MouseEvent) => {
    e.stopPropagation();
    const phone = PHONE_NUMBERS_MAP[painter.id];
    if (phone) window.location.href = `tel:${phone}`;
  };

  const handleDirections = (e: React.MouseEvent) => {
    e.stopPropagation();
    const lat = painter.serviceData?.geometry?.location.lat;
    const lng = painter.serviceData?.geometry?.location.lng;
    if (lat && lng) {
      window.open(`https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`, "_blank");
    }
  };

  return (
    <div
      onClick={() => onViewDetails(painter)}
      className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition cursor-pointer h-full flex flex-col"
    >
      {/* Image Carousel */}
      <div className="relative h-48 bg-gray-200 shrink-0">
        {currentPhoto ? (
          <>
            <img src={currentPhoto} alt={painter.title} className="w-full h-full object-cover" />
            {photos.length > 1 && (
              <>
                <button
                  onClick={handlePrevImage}
                  className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-2"
                >
                  <ChevronLeft size={20} />
                </button>
                <button
                  onClick={handleNextImage}
                  className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-2"
                >
                  <ChevronRight size={20} />
                </button>
                <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                  {currentImageIndex + 1}/{photos.length}
                </div>
              </>
            )}
          </>
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-400 text-4xl">
            <Paintbrush size={48} />
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4 space-y-2 flex-grow flex flex-col">
        <h2 className="text-xl font-bold text-gray-800 line-clamp-2">{painter.title}</h2>

        <div className="flex items-center gap-1 text-sm text-gray-600">
          <MapPin size={16} />
          <span className="line-clamp-1">{painter.location}</span>
        </div>

        {painter.distance && (
          <p className="text-xs font-semibold text-green-600">{painter.distance} km away</p>
        )}

        <p className="text-sm text-gray-600 line-clamp-3 mb-auto">{PAINTER_DESCRIPTIONS_MAP[painter.id] || painter.description}</p>

        {/* Rating & Status */}
        <div className="flex items-center gap-3 text-sm pt-2">
          {getRating() && (
            <div className="flex items-center gap-1">
              <Star size={14} className="fill-yellow-400 text-yellow-400" />
              <span className="font-semibold">{getRating()}</span>
              <span className="text-gray-500">({getUserRatingsTotal()})</span>
            </div>
          )}
          {isOpen !== undefined && (
            <div
              className={`flex items-center gap-1 px-2 py-0.5 rounded ${isOpen ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
                }`}
            >
              <Clock size={12} />
              <span className="text-xs font-semibold">{isOpen ? "Open" : "Closed"}</span>
            </div>
          )}
        </div>

        {/* Services */}
        <div className="pt-2">
          <p className="text-xs font-bold text-gray-500 uppercase mb-1">Services:</p>
          <div className="flex flex-wrap gap-2">
            {PAINTER_SERVICES.slice(0, 3).map((service, idx) => (
              <span
                key={idx}
                className="inline-flex items-center gap-1 bg-indigo-50 text-indigo-600 px-2 py-1 rounded text-xs"
              >
                ✓ {service}
              </span>
            ))}
          </div>
        </div>

        {/* Buttons */}
        <div className="flex gap-2 pt-4 mt-auto">
          <button
            onClick={handleDirections}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-indigo-50 text-indigo-700 border-2 border-indigo-600 rounded-lg hover:bg-indigo-100 font-semibold"
          >
            <Navigation size={16} />
            Directions
          </button>
          <button
            onClick={handleCall}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-green-50 text-green-700 border-2 border-green-600 rounded-lg hover:bg-green-100 font-semibold"
          >
            <Phone size={16} />
            Call
          </button>
        </div>
      </div>
    </div>
  );
};

const NearbyPainterCard: React.FC<NearbyPainterCardProps> = ({ painter, onViewDetails }) => {
  if (!painter) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {DUMMY_PAINTERS.map((p) => (
          <SinglePainterCard key={p.id} painter={p} onViewDetails={onViewDetails} />
        ))}
      </div>
    );
  }

  return <SinglePainterCard painter={painter} onViewDetails={onViewDetails} />;
};

export default NearbyPainterCard;
